import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { BooleanCell } from 'src/components/shared/DataGrid/BooleanCell';
import { BooleanFilterCell } from 'src/components/shared/DataGrid/BooleanFilterCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useIsFormDirty } from 'src/hooks/useIsFormDirty';
import { useReduxDispatch } from 'src/store';
import { PlantDetailDto, PlantDto } from 'src/store/plants/plant';
import { mapPlantDetailToEdit } from 'src/store/plants/plant.map';
import {
  createPlant,
  deletePlant,
  editPlant,
  fetchPlants
} from 'src/store/plants/plants.actions';
import { PlantApi } from 'src/store/plants/plants.api';
import { selectPlants } from 'src/store/plants/plants.selectors';
import { isLoadingPlants } from 'src/store/requests/requests.selectors';
import { PlantForm } from './PlantForm';

export const PlantsPage = () => {
  const [currentPlant, setCurrentPlant] = useState<PlantDto | null>(null);
  const [plant, setPlant] = useState<Nullable<PlantDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isFormDirty, callbackRef] = useIsFormDirty();

  const { t } = useTranslation();

  const dispatch = useReduxDispatch();

  const plants = useSelector(selectPlants);
  const loadingPlants = useSelector(isLoadingPlants);

  useEffect(() => {
    dispatch(fetchPlants());
  }, [dispatch]);

  useEffect(() => {
    if (currentPlant) {
      setShowDeleteModal(true);
    }
  }, [currentPlant]);

  const onCancel = () => {
    setCurrentPlant(null);
    setPlant(null);
    setShowDeleteModal(false);
    setShowEditModal(false);
  };

  const onCreate = () => {
    setShowEditModal(true);
  };

  const onEdit = async (plant: PlantDto) => {
    const response = await PlantApi.getById(plant.id);
    if (response.ok) {
      setPlant(response.data);
      setShowEditModal(true);
    }
  };

  const onDelete = (plant: PlantDto) => {
    setCurrentPlant(plant);
  };

  const onConfirmDelete = () => {
    if (currentPlant) {
      dispatch(deletePlant(currentPlant.id, currentPlant));
      setCurrentPlant(null);
      setShowDeleteModal(false);
    }
  };

  const onSubmit = async (plant: PlantDetailDto) => {
    const dto = mapPlantDetailToEdit(plant);
    if (dto.id) {
      await dispatch(editPlant(dto.id, dto));
      onCancel();
    } else {
      await dispatch(createPlant(dto));
      onCancel();
    }
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('plant.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('plant.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('plant.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={plants}
        loading={loadingPlants}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        initialSort={[{ field: 'no', dir: 'asc' }]}
      >
        <Column
          field="no"
          width={200}
          title={t('props.number')}
          footerCell={() => (
            <GridCountCell count={plants?.length} label={t('plant.plural')} />
          )}
        />
        <Column field="name" title={t('props.name')} />
        <Column field="description" title={t('props.description')} />
        <Column
          width="160px"
          field="active"
          title={t('props.active')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => <BooleanFilterCell {...props} />}
        />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        header={`${plant ? t('edit') : t('create.verb')} ${t(
          'plant.singular'
        )}`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <PlantForm
          plant={plant}
          onCancel={onCancel}
          onSubmit={onSubmit}
          ref={callbackRef}
        />
      </Modal>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('plant.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('plant.delete')} <q>{currentPlant?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
